import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("fa-IR", {
    maximumFractionDigits: 2,
  });

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("fa-IR", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const RemainingOrderBalancesChart = ({ balanceData = [] }) => {
  const currencies = useMemo(
    () => [...new Set(balanceData.map((row) => row.currency))],
    [balanceData],
  );
  const [currency, setCurrency] = useState("");
  const [viewMode, setViewMode] = useState("breakdown");

  useEffect(() => {
    if (!currencies.includes(currency)) {
      setCurrency(currencies[0] || "");
    }
  }, [currencies, currency]);

  const rows = useMemo(
    () => balanceData.filter((row) => row.currency === currency),
    [balanceData, currency],
  );

  const summary = useMemo(
    () =>
      rows.reduce(
        (totals, row) => ({
          remaining: totals.remaining + Number(row.remaining || 0),
          total: totals.total + Number(row.total || 0),
        }),
        { remaining: 0, total: 0 },
      ),
    [rows],
  );

  const data = {
    labels: rows.map((row) => row.order_no),
    datasets:
      viewMode === "remaining"
        ? [
            {
              label: "باقی‌مانده",
              data: rows.map((row) => row.remaining),
              backgroundColor: "rgba(16, 185, 129, 0.72)",
              borderColor: "rgb(5, 150, 105)",
              borderWidth: 1.5,
              borderRadius: 6,
            },
          ]
        : [
            {
              label: "مصرف‌شده",
              data: rows.map((row) => row.used),
              backgroundColor: "rgba(56, 189, 248, 0.72)",
              borderColor: "rgb(14, 165, 233)",
              borderWidth: 1.5,
              borderRadius: 6,
              stack: "balance",
            },
            {
              label: "باقی‌مانده",
              data: rows.map((row) => row.remaining),
              backgroundColor: "rgba(16, 185, 129, 0.76)",
              borderColor: "rgb(5, 150, 105)",
              borderWidth: 1.5,
              borderRadius: 6,
              stack: "balance",
            },
            {
              label: "رزروشده",
              data: rows.map((row) => row.booked),
              backgroundColor: "rgba(245, 158, 11, 0.7)",
              borderColor: "rgb(217, 119, 6)",
              borderWidth: 1.5,
              borderRadius: 6,
              stack: "booked",
            },
          ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: viewMode === "breakdown",
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 18,
          font: { family: "Vazirmatn", size: 12 },
        },
      },
      tooltip: {
        rtl: true,
        textDirection: "rtl",
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        padding: 12,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatNumber(context.raw)} ${currency}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        stacked: true,
        grid: { color: "rgba(148, 163, 184, 0.2)" },
        ticks: {
          callback: formatCompactNumber,
          font: { size: 11 },
        },
        title: {
          display: true,
          text: `مبلغ (${currency || "ارز"})`,
          color: "#334155",
          font: { size: 13, weight: "600" },
        },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: "Vazirmatn", size: 11 },
        },
        title: {
          display: true,
          text: "شماره ثبت سفارش",
          color: "#334155",
          font: { size: 13, weight: "600" },
        },
      },
    },
  };

  return (
    <section className="col-span-1 rounded-lg border border-slate-100 bg-white p-4 shadow-md sm:col-span-2 sm:p-6 lg:col-span-4">
      <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row-reverse sm:items-end sm:justify-between">
        <div className="text-right">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
            مانده ثبت سفارش‌ها
          </h2>
          <p className="mt-1 text-xs sm:text-sm text-slate-500">
            ده ثبت سفارش با بیشترین مانده
          </p>
        </div>

        <span className="inline-flex w-fit rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          وضعیت تخصیص ارز: تخصیص یافته
        </span>
      </div>

      <div
        dir="rtl"
        className="rounded-lg bg-gradient-to-b from-slate-50 to-white p-3 sm:p-4"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {currencies.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-700 sm:text-sm">
              <span className="font-medium">ارز:</span>
              <select
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                className="min-w-32 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 sm:text-sm"
              >
                {currencies.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <span className="text-slate-600">نمایش:</span>
            <div className="inline-flex rounded-full bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setViewMode("breakdown")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors sm:text-sm ${
                  viewMode === "breakdown"
                    ? "bg-sky-500 text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-200"
                }`}
              >
                ترکیب مانده
              </button>
              <button
                type="button"
                onClick={() => setViewMode("remaining")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors sm:text-sm ${
                  viewMode === "remaining"
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-200"
                }`}
              >
                فقط مانده
              </button>
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="flex min-h-64 items-center justify-center text-sm text-slate-500">
            داده‌ای برای نمایش وجود ندارد
          </div>
        ) : (
          <div
            className="w-full"
            style={{ height: `${Math.max(320, rows.length * 54)}px` }}
          >
            <Bar data={data} options={options} />
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs font-semibold text-slate-700 sm:text-sm">
          <span>
            مجموع مانده: {formatNumber(summary.remaining)} {currency}
          </span>
          <span className="text-slate-500">
            {rows.length.toLocaleString("fa-IR")} ثبت سفارش از مجموع {formatNumber(summary.total)} {currency}
          </span>
        </div>
      </div>
    </section>
  );
};

RemainingOrderBalancesChart.propTypes = {
  balanceData: PropTypes.arrayOf(
    PropTypes.shape({
      order_no: PropTypes.string,
      currency: PropTypes.string,
      total: PropTypes.number,
      used: PropTypes.number,
      remaining: PropTypes.number,
      booked: PropTypes.number,
    }),
  ),
};

export default RemainingOrderBalancesChart;
